import {
  ColumnHeaderStyles,
  SimpleRowStyles,
  WeekToDateStyles,
  ShiftRowStyles,
  CustomHeaderStyle,
  backgroundColors,
} from "./weekToDateStyles";
import { getShiftNumbers } from "reports/pages/utils";
import { round } from "lodash";
import WeeklyHeader from "./WeeklyHeader";
import { useGetWeekToDateProps } from "./WeekToDateUtils";
import {
  DataDumpAggregateReportsType,
  shiftDayWeekTimeRanges,
} from "../../../types";
import { NUM_DAYS_IN_WEEK } from "reports/utils/timeUtils";
import { useTranslation } from "react-i18next";

const formatMaybeNumber = (n?: number | null) =>
  n !== null && n !== undefined ? formatNumber(n) : "";
const formatNumber = (n: number) => new Intl.NumberFormat().format(n);

const ColumnHeader = ({ t }) => (
  <ColumnHeaderStyles>
    <div className="row-label-column" />
    <div className="day-column">{t("mon")}</div>
    <div className="day-column">{t("tue")}</div>
    <div className="day-column">{t("wed")}</div>
    <div className="day-column">{t("thu")}</div>
    <div className="day-column">{t("fri")}</div>
    <div className="day-column">{t("sat")}</div>
    <div className="day-column">{t("sun")}</div>
    <div className="day-column">{t("total")}</div>
  </ColumnHeaderStyles>
);

type SimpleRowType = {
  rowLabel: string;
  dailyValues: string[];
  totalValue?: string | null;
};

const SimpleRow = ({
  rowLabel,
  dailyValues,
  totalValue = null,
}: SimpleRowType) => {
  // if we don't have 7 values in a week, we need to append empty values so that we can complete the whole 7
  const emptyValuesToAdd = NUM_DAYS_IN_WEEK - dailyValues.length;

  // if we are required to add empty values
  if (emptyValuesToAdd !== 0) {
    // loop adding an empty value for each time we need one
    for (let valuesToAdd = 0; valuesToAdd < emptyValuesToAdd; valuesToAdd++) {
      dailyValues.push(""); // push empty values
    }
  }

  const { t } = useTranslation("WeeklyAggregate");
  // if our rowlabel is actual that means it's an output of units
  return rowLabel === "actual" ? (
    <SimpleRowStyles>
      <div className="row-label-column actual">{t(rowLabel)}</div>
      {dailyValues.map(dailyValue => (
        <div
          key={`${dailyValue}-${Math.random()}`}
          className="day-column actual"
        >
          {dailyValue}
        </div>
      ))}
      <div className="total-column actual">{totalValue}</div>
    </SimpleRowStyles>
  ) : (
    <SimpleRowStyles>
      <div className="row-label-column oee">{t(rowLabel)}</div>
      {dailyValues.map(dailyValue => (
        <div key={`${dailyValue}-${Math.random()}`} className="day-column oee">
          {dailyValue}
        </div>
      ))}
      <div className="total-column oee">{totalValue}</div>
    </SimpleRowStyles>
  );
};

const SingleShiftRow = ({
  shiftName,
  shiftOutput,
  shiftOee,
  weeklyOutput,
  weeklyOee,
  rowIndex,
}) => (
  <div>
    <div
      className="shift-row"
      style={{ backgroundColor: backgroundColors[rowIndex % 2] }}
    >
      <div className="subtitle-wrapper">
        <div className="shift-name-subtitle">{shiftName}</div>
      </div>
      <div className="shift-row-inner">
        <SimpleRow
          rowLabel={"actual"}
          dailyValues={shiftOutput}
          totalValue={weeklyOutput ? weeklyOutput : ""}
        />
        <SimpleRow
          rowLabel={"OeePercent"}
          dailyValues={shiftOee.map(({ oeePercent, oeePercentTopDown }) =>
            oeePercent
              ? oeePercent.toFixed(1)
              : oeePercent !== null
                ? oeePercent
                : oeePercentTopDown
                  ? oeePercentTopDown.toFixed(1)
                  : "-"
          )}
          totalValue={weeklyOee ? weeklyOee : ""}
        />
      </div>
    </div>
  </div>
);

const ShiftRow = ({
  uniqueShiftNames,
  shiftOutputs,
  shiftOees,
  weeklyOutputs,
  weeklyOees,
  t,
}) => (
  <ShiftRowStyles>
    {uniqueShiftNames.map((shiftName, index) => {
      return (
        <SingleShiftRow
          key={shiftName}
          shiftName={shiftName}
          shiftOutput={shiftOutputs[index]}
          shiftOee={shiftOees[index]}
          weeklyOutput={weeklyOutputs[index]}
          weeklyOee={weeklyOees[index]}
          rowIndex={index}
        />
      );
    })}
  </ShiftRowStyles>
);

const CustomHeaderComponent = ({ header, content, footer }) => {
  return (
    <CustomHeaderStyle>
      <div className="text">{header}</div>
      <div className="text main-content">
        {content}
        <span className="small-percent">%</span>
      </div>

      <div className="text">{footer}</div>
    </CustomHeaderStyle>
  );
};

type timeZoneContext = {
  timeZone: string;
  year: number;
  weekNumber: number;
  dayStartHour: number;
  dayStartMinute: number;
};

type WeekToDateProps = {
  processName: string;
  dataRetrievedAt: number;
  dateTimeContext: timeZoneContext;
  timeRanges: shiftDayWeekTimeRanges;
  uniqueShiftName: (string | undefined)[];
  dataByShift: DataDumpAggregateReportsType[];
  dataByDay: DataDumpAggregateReportsType[];
  dataByWeek: DataDumpAggregateReportsType[];
  oeeTargetPercent: number;
  shouldShowCloseTab: boolean;
  shouldShowTranslate?: boolean;
};

export default function WeekToDate({
  processName,
  dataRetrievedAt,
  dateTimeContext,
  timeRanges,
  uniqueShiftName,
  dataByShift,
  dataByDay,
  dataByWeek,
  oeeTargetPercent,
  shouldShowCloseTab = false,
  shouldShowTranslate,
}: WeekToDateProps) {
  const {
    activeDays,
    custName,
    custLocation,
    shouldHideTarget,
    shouldHideCumulativeTargets,
  } = useGetWeekToDateProps();

  // calculate how many maximum possible units produced we have
  const dailyTargets = dataByDay.map(({ productionPotentialOutput }) => {
    return round(productionPotentialOutput);
  });

  const dailyTotals = dataByDay.map(({ netOutput }) => netOutput);
  const weekTotal = dailyTotals.reduce((agg, t) => (t ? agg + t : agg), 0);
  let cummulative = 0;
  const cummulativeTotals = dailyTotals.map(t => {
    cummulative += t;
    return cummulative;
  });

  cummulative = 0;
  const cummulativeTargets = dailyTargets.map((t, index) => {
    return index < activeDays ? (cummulative += t) : cummulative;
  });

  // sum up the total of the daily targets
  const weekTarget = dailyTargets.reduce((agg, entry) => {
    return agg + entry;
  }, 0);

  const ShiftInTheFuture = uniqueShiftName.map((value, index) =>
    timeRanges.shifts.map(shifts => +shifts[index]?.ge > dataRetrievedAt)
  );
  const dayInTheFuture = timeRanges.days.map(({ ge }) => +ge > dataRetrievedAt);

  const { shiftOutputs, shiftOees, weeklyOees, weeklyOutputs } =
    getShiftNumbers(dataByShift, uniqueShiftName);

  const { t } = useTranslation("WeeklyAggregate");
  return (
    <WeekToDateStyles>
      <WeeklyHeader
        {...dateTimeContext}
        {...timeRanges.week}
        custName={custName}
        custLocation={custLocation}
        oeeTarget={oeeTargetPercent}
        dataRetrievedAt={dataRetrievedAt}
        processName={processName}
        customComponent={
          shouldHideTarget ? null : (
            <CustomHeaderComponent
              header={t("OEE")}
              content={oeeTargetPercent}
              footer={t("target")}
            />
          )
        }
        shouldShowCloseTab={shouldShowCloseTab}
        shouldShowTranslate={shouldShowTranslate}
      />
      <ColumnHeader t={t} />
      <div className="column-header">
        {!shouldHideCumulativeTargets && (
          <SimpleRow
            rowLabel={t("target")}
            dailyValues={dailyTargets.map((v, index) =>
              index < activeDays ? formatMaybeNumber(v) : "0"
            )}
            totalValue={formatMaybeNumber(weekTarget)}
          />
        )}
      </div>
      <ShiftRow
        {...{
          uniqueShiftNames: uniqueShiftName,
          shiftOutputs: shiftOutputs.map((shiftOutput, shiftIndex) => {
            return shiftOutput
              .map(element => formatMaybeNumber(element))
              .map((v, idx) => (ShiftInTheFuture[shiftIndex][idx] ? "" : v));
          }),
          shiftOees: shiftOees.map((element, oeeIndex) =>
            element.map((v, idx) => (ShiftInTheFuture[oeeIndex][idx] ? "" : v))
          ),
          weeklyOutputs: weeklyOutputs.map(weeklyOutput => {
            return weeklyOutput ? formatMaybeNumber(weeklyOutput) : "-";
          }),
          weeklyOees: weeklyOees,
          t,
        }}
      />
      <SimpleRow
        rowLabel={t("dailyTotal")}
        dailyValues={dailyTotals
          .map(v => formatMaybeNumber(v))
          .map((v, idx) => (dayInTheFuture[idx] ? "" : v))}
        totalValue={formatMaybeNumber(weekTotal)}
      />
      <SimpleRow
        rowLabel={t("cumulativeActual")}
        dailyValues={cummulativeTotals
          .map(v => formatMaybeNumber(v))
          .map((v, idx) => (dayInTheFuture[idx] ? "" : v))}
        totalValue={formatMaybeNumber(weekTotal)}
      />
      {!shouldHideCumulativeTargets && (
        <SimpleRow
          rowLabel={t("cumulativeTarget")}
          dailyValues={cummulativeTargets.map(v => formatMaybeNumber(v))}
          totalValue={formatMaybeNumber(weekTarget)}
        />
      )}
      <SimpleRow
        rowLabel={t("dailyOeePercent")}
        dailyValues={dataByDay
          .map(({ oeePercent, oeePercentTopDown }) =>
            oeePercent
              ? oeePercent.toFixed(1)
              : oeePercentTopDown
                ? oeePercentTopDown.toFixed(1)
                : "-"
          )
          .map((v, idx) => (dayInTheFuture[idx] ? "" : v))}
        totalValue={
          dataByWeek[0].oeePercent
            ? dataByWeek[0].oeePercent.toFixed(1)
            : dataByWeek[0].oeePercentTopDown
              ? dataByWeek[0].oeePercentTopDown.toFixed(1)
              : "-"
        }
      />
    </WeekToDateStyles>
  );
}
